/**
 * Tier badge helper - single source of truth for the emoji + colour treatment
 * shown next to every account name (in their profile card and in every
 * comment they leave).
 *
 * Mapping (per product decision Feb 2026):
 *   • vip       → 🎫  gold, glowing (admin-comped access - same look as founder)
 *   • founding  → 👑  gold, glowing
 *   • annual    → 🪙  gold coin (no glow)
 *   • monthly   → 🤝  handshake (no glow)
 *   • free / anon → no emoji, plain white
 *
 * Callers should treat a null/empty plan as "free".
 */
export const getTierBadge = (plan) => {
  switch (plan) {
    case 'vip':
      return {
        emoji: '🎫',
        label: 'VIP',
        nameClass: 'text-yellow-300',
        nameStyle: { textShadow: '0 0 10px rgba(250, 204, 21, 0.55)' },
        emojiClass:
          'inline-block drop-shadow-[0_0_6px_rgba(250,204,21,0.75)] animate-[pulse_2.4s_ease-in-out_infinite]',
        pillClass:
          'bg-yellow-500/15 text-yellow-300 border border-yellow-500/60',
      };
    case 'founding':
      return {
        emoji: '👑',
        label: 'Founding Member',
        // Name renders in gold with a soft glow so founders stand out in
        // long comment threads at a glance.
        nameClass: 'text-yellow-300',
        nameStyle: { textShadow: '0 0 10px rgba(250, 204, 21, 0.55)' },
        emojiClass:
          'inline-block drop-shadow-[0_0_6px_rgba(250,204,21,0.75)] animate-[pulse_2.4s_ease-in-out_infinite]',
        pillClass:
          'bg-yellow-500/15 text-yellow-300 border border-yellow-500/60',
      };
    case 'annual':
      return {
        emoji: '🪙',
        label: 'Annual Subscriber',
        nameClass: 'text-white',
        nameStyle: null,
        emojiClass: 'inline-block',
        pillClass:
          'bg-amber-500/10 text-amber-300 border border-amber-500/50',
      };
    case 'monthly':
      return {
        emoji: '🤝',
        label: 'Monthly Subscriber',
        nameClass: 'text-white',
        nameStyle: null,
        emojiClass: 'inline-block',
        pillClass:
          'bg-emerald-500/10 text-emerald-300 border border-emerald-500/50',
      };
    default:
      return {
        emoji: '',
        label: 'No subscription',
        nameClass: 'text-white',
        nameStyle: null,
        emojiClass: '',
        pillClass: 'bg-zinc-800/60 text-zinc-400 border border-zinc-700',
      };
  }
};

/** Effective tier for badge/name rendering.
 *  VIP (comp_access) overrides the underlying paid plan - an admin-comped
 *  reader is displayed as a VIP even if their base plan is still "free". */
export const effectivePlan = (user) => {
  if (!user) return null;
  if (user.author_comp_access || user.comp_access) return 'vip';
  return user.author_plan || user.plan || null;
};
